import {
  Container,
  Divider,
  Stack,
  Typography,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { themeOrange, themeBlue } from "../../utils/colorTheme";
import CustomButton from "../../component/common/Button/Button";
import { MdSend, MdNotifications, MdHistory } from "react-icons/md";
import { broadcastNotificationAsync, getNotificationsHistoryAsync } from "../../services/AdminServiceManage";
import { success_toast_message, error_toast_message } from "../../utils/toast_message";

const DashboardNotifications = () => {
  const dispatch = useDispatch();
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState({
    title: "",
    message: "",
    audience: "all",
  });

  const [notificationHistory, setNotificationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const rows = await dispatch(getNotificationsHistoryAsync()).unwrap();
      setNotificationHistory(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.log("history load failed:", e?.message);
      setNotificationHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleChange = (field) => (event) => {
    setNotification({ ...notification, [field]: event.target.value });
  };

  const handleSend = async () => {
    if (!notification.title.trim() || !notification.message.trim()) {
      error_toast_message("Please fill in title and message");
      return;
    }

    setSending(true);
    try {
      const result = await dispatch(
        broadcastNotificationAsync({
          title: notification.title.trim(),
          body: notification.message.trim(),
          audience: notification.audience,
        })
      ).unwrap();
      if (result?.status) {
        const r = result.result || {};
        success_toast_message(
          `Sent to ${r.sent || 0} • failed ${r.failed || 0} • skipped ${r.skipped || 0} (no device token)`
        );
        setNotification({ title: "", message: "", audience: "all" });
        await fetchHistory();
      } else {
        error_toast_message(result?.message || "Broadcast failed");
      }
    } catch (e) {
      error_toast_message(e?.message || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAudienceChip = (audience) => {
    const config = {
      all: { label: "Everyone", color: themeBlue, bg: "#e8f4fd" },
      users: { label: "Customers", color: "#22c55e", bg: "#dcfce7" },
      pros: { label: "Pros", color: themeOrange, bg: "#fff4e6" },
      // legacy value seen in old mock data, keep the chip from breaking
      vendors: { label: "Pros", color: themeOrange, bg: "#fff4e6" },
    };
    const c = config[audience] || config.all;
    return (
      <Chip
        label={c.label}
        size="small"
        sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600 }}
      />
    );
  };

  return (
    <Stack bgcolor={"#FAFAFA"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        my={1}
      >
        <Typography className="mainHeading">Notifications</Typography>
      </Stack>
      <Divider />

      <Container
        sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}
      >
        <Stack my={3}>
          <Grid container spacing={3}>
            {/* Send Notification */}
            <Grid item xl={5} lg={5} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} mb={3}>
                  <MdNotifications size={24} color={themeOrange} />
                  <Typography
                    className="mainHeading"
                    sx={{ fontSize: "20px !important" }}
                  >
                    Send Notification
                  </Typography>
                </Stack>

                <Stack gap={2.5}>
                  <TextField
                    label="Title"
                    value={notification.title}
                    onChange={handleChange("title")}
                    fullWidth
                    size="small"
                    placeholder="Enter notification title"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />

                  <TextField
                    label="Message"
                    value={notification.message}
                    onChange={handleChange("message")}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter notification message"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />

                  <FormControl fullWidth size="small">
                    <InputLabel>Audience</InputLabel>
                    <Select
                      value={notification.audience}
                      label="Audience"
                      onChange={handleChange("audience")}
                      sx={{ borderRadius: "8px" }}
                    >
                      <MenuItem value="all">Everyone (Customers + Pros)</MenuItem>
                      <MenuItem value="users">Customers only</MenuItem>
                      <MenuItem value="pros">Pros only</MenuItem>
                    </Select>
                  </FormControl>

                  <Stack
                    className="globleGradientBlue"
                    sx={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <CustomButton
                      style={{
                        backgroundColor: "transparent",
                        fontSize: "16px",
                        fontWeight: "500",
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                      onClick={handleSend}
                      disabled={sending}
                    >
                      {sending ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                      ) : (
                        <>
                          <MdSend size={20} />
                          <Typography sx={{ color: "white" }}>Send Notification</Typography>
                        </>
                      )}
                    </CustomButton>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Notification History */}
            <Grid item xl={7} lg={7} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} mb={3}>
                  <MdHistory size={24} color={themeBlue} />
                  <Typography
                    className="mainHeading"
                    sx={{ fontSize: "20px !important" }}
                  >
                    Notification History
                  </Typography>
                </Stack>

                <Stack gap={2}>
                  {notificationHistory.map((item) => (
                    <Stack
                      key={item.id}
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #e5e5e5",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Stack>
                          <Typography sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                          <Typography sx={{ color: "#666", fontSize: "14px", mt: 0.5 }}>
                            {item.body ?? item.message}
                          </Typography>
                        </Stack>
                        {getAudienceChip(item.audience)}
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1.5}
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Typography sx={{ color: "#999", fontSize: "12px" }}>
                          {formatDate(item.created_at ?? item.sent_at)}
                        </Typography>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          <Chip label={`Sent ${item.sent_count ?? item.recipients ?? 0}`} size="small" color="success" variant="outlined" />
                          {(item.failed_count ?? 0) > 0 && (
                            <Chip label={`Failed ${item.failed_count}`} size="small" color="error" variant="outlined" />
                          )}
                          {(item.skipped_count ?? 0) > 0 && (
                            <Chip label={`No token ${item.skipped_count}`} size="small" variant="outlined" />
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardNotifications;
